/**
 * The individual official profile — module 05, `04-officials-profiles.md` Part B.
 *
 * `/officials/:officialId`.
 *
 * **Run 21C — conformance to the agreed mockup.** The signed-off design,
 * `docs/design/official-profile-detail-v1.html`, was outside the repository until today, and
 * the page built from the specification's prose had drifted a long way from it. It is rebuilt
 * here section by section to §B1's layout:
 *
 * ```
 * .profgrid  (65fr | 35fr)
 *   LEFT  .stackcol   Profile · Appointed Duties · Teams Coverage · Co-Officials Matrix
 *   RIGHT .stackcol   Official Experience · Quick Notes · Cards Issued (umpires only)
 *                     · Past Experience / Expectation · Report Readiness
 * .repband            Reporting Section
 * panel               Marking (full width)
 * .apptsum (5fr | 7fr)  LEFT TD Feedback · RIGHT the report panels
 * ```
 *
 * Every panel the mockup draws is on the page, in its position, with its heading, note and
 * foot. Where this build has no store behind one, the panel says so in a plain sentence and
 * draws nothing — never sample rows, never a zero that would read as a finding about the
 * person. The individual cases are argued in `features/officials/ProfileDetailPanels.tsx`.
 *
 * **`Tournament Report` is gone from this page, and nothing was lost.** It is the Technical
 * Delegate's end-of-tournament report to FIH — about the tournament, not about the person
 * whose profile this is — so it belongs on Reporting and on Tournament Configuration. It is
 * already on the Reporting page's own heading band and in the masthead of every screen, so
 * the link is one click away from here and is not duplicated on a surface it does not
 * describe. **`Report Studio` takes its place**, and opens this official's own report
 * drafting.
 *
 * **Five reads, all `requireTd`-guarded.** The identity and contact details come from
 * `GET /officials/:tmsPersonId` — the profile projection, which carries the date of birth and
 * the contact details the roster list strips. The marking, the report and the receipts come
 * from the three routes the marking screen already uses.
 *
 * **The fifth is this page's own: `GET /officials/:tmsPersonId/appointments`** — §B3's
 * `Appointed Duties`, every match this person is down for across the tournament, technical and
 * umpire alike. It is the one route added for this page, and it was added rather than derived
 * on the client for the reason §B3 went undrawn for so long: the answer is a join of two
 * appointment datasets that Part C invariant 5 says must never be merged carelessly, against
 * the whole schedule, and a browser assembling it would be a second opinion on which rows
 * count as a duty. See `apps/api/src/officials/appointed-duties.ts`.
 *
 * **They are fetched independently on purpose.** A refused marking read must not take the
 * profile down with it — the TD came to look at a person, and the panel that failed says so
 * where it failed.
 *
 * **The TD's own profile carries no Reporting Section at all (ruling R2′).** Nothing in the
 * system ever points a report or a mark at the TD, so the three reporting reads are not made
 * at all rather than made and 404-ed.
 *
 * **Ruling R-4′.** Nothing about a UM's unreleased notes appears anywhere on this page, in
 * any form — no count, no timestamp, no content, and not the existence badge either. Run 30's
 * `Quick Notes` panel reads the TD's OWN stream (`GET /td-notes`, `requireTd`, migration 037's
 * `td_note`), which is a different table behind a different guard from the UM notes; no UM note
 * route is read on this page.
 */

import { Link, useParams } from "react-router-dom";

import {
  ApiError,
  fetchMarkingPanel,
  fetchOfficial,
  fetchOfficialAppointments,
  fetchPushReceipts,
  fetchReport,
} from "../api/client.js";
import type { MarkingPanelBody, PushReceiptListBody, ReportBody } from "../api/types.js";
import { usePageKicker } from "../app/pageKicker.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { useAsync } from "../app/useAsync.js";
import {
  AppointedDutiesPanel,
  CardsIssuedPanel,
  CoOfficialsPanel,
  ExperiencePanel,
  PastExperiencePanel,
  QuickNotesPanel,
  ReportReadinessPanel,
  TdFeedbackPanel,
  TeamsCoveragePanel,
  useTdNotes,
} from "../features/officials/ProfileDetailPanels.js";
import { MarksSummaryPanel } from "../features/officials/MarksSummaryPanel.js";
import { ProfileHeader } from "../features/officials/ProfileHeader.js";
import { ProfilePanel } from "../features/officials/ProfilePanel.js";
import { PushReceiptsPanel } from "../features/officials/PushReceiptsPanel.js";
import { ReportStatusPanel } from "../features/officials/ReportStatusPanel.js";

/** §B1's Reporting Section lead, verbatim from the mockup. */
export const REPORTING_SECTION_LEAD =
  "The marking panel mirrors the TMS grading form; the outcome recomputes live as you move " +
  "a mark. Feedback and the AI draft below feed the final report.";

export function OfficialProfilePage(): React.JSX.Element {
  const { officialId } = useParams<{ officialId: string }>();
  const id = officialId ?? "";

  const official = useAsync(() => fetchOfficial(id), [id]);
  const subject = official.status === "ready" ? official.data : null;

  /**
   * The TD's own notes on this official, read ONCE for the whole page (run 51).
   *
   * Three panels draw from it — `Quick Notes` lists it, `TD Feedback` groups it by grading
   * category and `Report Readiness` counts the categories it covers — so one read is what keeps
   * the three from disagreeing on one screen, and a correction saved in the notebook moves the
   * grouping and the ring with it. The route key is the same `:officialId` every other read on
   * this page uses, which is the TMS person id.
   */
  const tdNotes = useTdNotes(id);

  /**
   * §B3's `Appointed Duties`, read on its own and for EVERYBODY.
   *
   * **Not gated on `reportable`, and ruling R2′ does not reach it.** What R2′ forbids is a
   * report or a mark pointed at the Technical Delegate; where a person is down to be all
   * fortnight is neither, and the TD holds duties of his own. The read is made on every
   * profile, exactly as the identity read above is.
   *
   * A read of its own, as every other read on this page is: a refused duties read leaves the
   * identity card, the marking and the report exactly where they are, and says so in the one
   * panel it belongs to.
   */
  const duties = useAsync(() => fetchOfficialAppointments(id), [id]);

  /** Everybody except the TD (ruling R2′), and only once we know which this is. */
  const reportable = subject !== null && subject.role !== "TD";

  // §B's kicker — `{Role} · {CTRY} · Profile, Appointments And Reporting`. Claimed only once
  // the official is known: a half-composed line flashing over the masthead would be worse
  // than the module's own, which is at least true while this page loads.
  usePageKicker(
    subject === null
      ? null
      : `${subject.role} · ${subject.country} · Profile, Appointments And Reporting`,
  );

  const panel = useAsync<MarkingPanelBody | null>(
    () => (reportable ? fetchMarkingPanel(id) : Promise.resolve(null)),
    [id, reportable],
  );
  const report = useAsync<ReportBody | null>(
    () => (reportable ? fetchReport(id) : Promise.resolve(null)),
    [id, reportable],
  );
  const receipts = useAsync<PushReceiptListBody | null>(
    () => (reportable ? fetchPushReceipts(id) : Promise.resolve(null)),
    [id, reportable],
  );

  /**
   * A 403 is not a fault and is not dressed as one. The credential is good; this surface is
   * simply not the holder's — every profile route is `requireTd`, because the projection
   * carries a date of birth and contact details. A 401 is the session's business and is
   * announced once, by the API client, as the signed-out screen.
   */
  if (
    official.status === "error" &&
    official.error instanceof ApiError &&
    official.error.status === 403
  ) {
    return (
      <section className="panel panel--d1" style={{ marginTop: 22 }} aria-labelledby="profDenyTitle">
        <div className="panel__head">
          <h3 className="panel__title" id="profDenyTitle">
            A Profile Is The Technical Delegate&rsquo;s
          </h3>
          <span className="panel__note">Signed In · Not Permitted Here</span>
        </div>
        <div className="emptystate">
          <b>This Device Cannot Open A Profile</b>
          A profile carries a date of birth, an email address and a telephone number, so the
          route behind this page is refused to an Umpires Manager credential. Nothing is wrong
          with this device&rsquo;s sign-in; it is still signed in and every other screen it is
          entitled to is unaffected.
        </div>
        <div className="panel__foot">{official.error.message}</div>
      </section>
    );
  }

  const markingPath = `/marking/${id}`;
  const reportStudioPath = `${markingPath}/report`;
  const isUmpire = subject !== null && subject.role === "Umpire";

  return (
    <>
      <div className="repband">
        <h2>
          {subject === null ? "Official Profile" : subject.mediaName}
          {subject === null ? null : <span className="md"> · Official Profile</span>}
        </h2>
        <HowToUse id="official-profile" title="How to use an Official Profile">
          <li>
            <HowToLink to="profile-panel">Profile</HowToLink> is who this official is and how to
            reach them, as TMS holds it.
          </li>
          <li>
            The left column follows them through this tournament. <b>Appointed Duties</b> is
            every match they are down for, technical or umpire, in date order, each saying
            whether it has been played —{" "}
            <HowToLink to="appointment-table">Appointed Duties</HowToLink>,{" "}
            <HowToLink to="teams-coverage">Teams Coverage</HowToLink> and{" "}
            <HowToLink to="co-officials">Co-Officials Matrix</HowToLink>.
          </li>
          <li>
            The right column is their background and your evidence —{" "}
            <HowToLink to="experience">Official Experience</HowToLink>,{" "}
            <HowToLink to="quick-notes">Quick Notes</HowToLink>,{" "}
            <HowToLink to="past-experience">Past Experience / Expectation</HowToLink> and{" "}
            <HowToLink to="report-readiness">Report Readiness</HowToLink>.
          </li>
          <li>
            <HowToLink to="marks-summary">Marking</HowToLink> shows their one
            whole-tournament mark. Use <b>Marking Panel</b> to change it.
          </li>
          <li>
            <HowToLink to="report-status">Report</HowToLink> shows how far their report has got.
            Use <b>Report Studio</b> in this heading band to write it — that is where the
            marking panel, your feedback, the AI draft and your own draft sit together.
          </li>
          <li>
            <HowToLink to="push-receipts">Push Receipts</HowToLink> records every press that
            sent marks or a report to TMS, what TMS said back in its own words, and who pressed
            it. One press can write two rows, because marks and report are answered separately
            and either can fail while the other lands.
          </li>
          <li>
            A stage that did not move after a push means that half did not land. A panel with no
            record behind it yet says so in words rather than showing a nought.
          </li>
        </HowToUse>
        <span style={{ flex: 1 }} />
        <Link className="linkbtn-ext" to="/officials">
          Officials Profiles
        </Link>
        {reportable ? (
          <Link className="linkbtn-ext" to={reportStudioPath} data-testid="report-studio-link">
            Report Studio
          </Link>
        ) : null}
      </div>

      {official.status === "loading" ? <Skeleton rows={4} /> : null}
      {official.status === "error" ? <FailBanner error={official.error} /> : null}

      {subject === null ? null : (
        <>
          <ProfileHeader
            official={subject}
            reportable={reportable}
            stage={report.status === "ready" && report.data !== null ? report.data.stage : null}
            ownedByUm={report.status === "ready" && report.data !== null && report.data.ownedByUm}
            ownerUmShortName={
              report.status === "ready" && report.data !== null
                ? report.data.ownerUmShortName
                : null
            }
            outcome={
              panel.status === "ready" && panel.data !== null ? panel.data.outcome : undefined
            }
          />

          {/* §B1's grid — the mockup's left column, then its right column, in its order. */}
          <div className="profgrid">
            <div className="stackcol">
              <ProfilePanel official={subject} />
              {duties.status === "loading" ? <Skeleton rows={4} /> : null}
              {duties.status === "error" ? <FailBanner error={duties.error} role="status" /> : null}
              <AppointedDutiesPanel duties={duties.status === "ready" ? duties.data : null} />
              <TeamsCoveragePanel />
              <CoOfficialsPanel />
            </div>

            <div className="stackcol">
              <ExperiencePanel official={subject} />
              <QuickNotesPanel
                tmsPersonId={subject.tmsPersonId}
                role={subject.role}
                notes={tdNotes}
              />
              {/* §B8 — removed from the DOM entirely for anyone but an umpire. */}
              {isUmpire ? <CardsIssuedPanel /> : null}
              <PastExperiencePanel official={subject} />
              <ReportReadinessPanel role={subject.role} notes={tdNotes} />
            </div>
          </div>

          {/* ================= REPORTING SECTION ================= */}
          {reportable ? (
            <>
              <div className="repband" data-testid="reporting-section-band">
                <h2>Reporting Section</h2>
                <p>{REPORTING_SECTION_LEAD}</p>
                <span style={{ flex: 1 }} />
                <Link className="linkbtn-ext" to={reportStudioPath}>
                  Report Studio
                </Link>
              </div>

              {panel.status === "loading" ? <Skeleton rows={6} /> : null}
              {panel.status === "error" ? <FailBanner error={panel.error} /> : null}
              {panel.status === "ready" && panel.data !== null ? (
                <MarksSummaryPanel panel={panel.data} markingPath={markingPath} />
              ) : null}

              <div className="apptsum">
                <TdFeedbackPanel role={subject.role} notes={tdNotes} />

                <div className="stackcol">
                  {report.status === "loading" ? <Skeleton rows={4} /> : null}
                  {report.status === "error" ? <FailBanner error={report.error} /> : null}
                  {report.status === "ready" && report.data !== null ? (
                    <ReportStatusPanel
                      report={report.data}
                      markingPath={markingPath}
                      reportStudioPath={reportStudioPath}
                    />
                  ) : null}

                  {receipts.status === "loading" ? <Skeleton rows={3} /> : null}
                  {receipts.status === "error" ? <FailBanner error={receipts.error} /> : null}
                  {receipts.status === "ready" && receipts.data !== null ? (
                    <PushReceiptsPanel receipts={receipts.data.receipts} />
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <section
              className="panel panel--d2"
              aria-labelledby="noReportingTitle"
              data-testid="no-reporting-section"
              style={{ marginTop: 26 }}
            >
              <div className="panel__head">
                <h3 className="panel__title" id="noReportingTitle">
                  No Reporting Section
                </h3>
                <span className="panel__note">Ruling R2&prime;</span>
              </div>
              <div className="emptystate">
                <b>The TD receives no report and no marks.</b>
                Nothing in the system ever points a report or a mark at the Technical Delegate,
                so this profile carries no marking, no report and no stage — and he is in no
                report denominator either. There is nothing missing here.
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
